// src/pages/RoomTypesPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Badge,
  Modal,
  notification,
  Breadcrumb,
  Input,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import "./RoomTypesPage.css";

const { Text, Title } = Typography;
const { confirm } = Modal;

/** Slim / neat styling (mirrors RoomListPage) */
const slimTableStyles = `
  .rt-slim-table .ant-table-thead > tr > th{
    padding: 6px 8px !important;
    font-size: 12px !important;
    font-weight: 600;
    background: #fafafa;
    white-space: nowrap;
  }

  .rt-slim-table .ant-table-tbody > tr > td{
    padding: 6px 8px !important;
    font-size: 12px !important;
    vertical-align: top;
  }

  .rt-slim-table .ant-tag{
    margin: 0 !important;
    font-size: 11px !important;
    padding: 0px 6px !important;
    border-radius: 10px !important;
    line-height: 18px;
  }

  .rt-slim-table .ant-btn-sm{
    height: 24px !important;
    padding: 0 8px !important;
    font-size: 12px !important;
    border-radius: 6px !important;
  }

  .rt-slim-table .ant-btn-icon-only.ant-btn-sm{
    width: 26px !important;
    padding: 0 !important;
  }

  .rt-slim-table .ant-space{
    gap: 6px !important;
  }

  .rt-slim-table .ant-table-pagination{
    margin: 12px 0 !important;
  }

  .rt-header-card .ant-card-body{
    padding: 12px !important;
  }

  .rt-header-row{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:12px;
  }

  .rt-header-title{
    display:flex;
    flex-direction:column;
    gap:2px;
    min-width:240px;
  }

  .rt-actions{
    display:flex;
    justify-content:flex-end;
    gap:8px;
    flex-wrap:wrap;
  }

  .rt-filters{
    margin-top: 10px;
    display:flex;
    gap:8px;
    flex-wrap:wrap;
    align-items:center;
  }

  .rt-filters .ant-input{
    height: 34px;
  }
`;

const RoomTypesPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const hotelId = authData?.hotelId;

  const [roomTypes, setRoomTypes] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [showGuide, setShowGuide] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [currentRoomType, setCurrentRoomType] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [description, setDescription] = useState("");

  // Inject styles once (same approach as RoomListPage)
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = slimTableStyles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  useEffect(() => {
    if (hotelId) return;
    navigate("/login");
  }, [hotelId, navigate]);

  const breadcrumbItems = useMemo(
    () => [
      {
        title: (
          <Link
            to={
              authData?.role?.toLowerCase() === "systemadmin"
                ? "/admin/dashboard"
                : "/hotels/dashboard"
            }
          >
            Home
          </Link>
        ),
      },
      { title: "Room Types" },
    ],
    [authData?.role]
  );

  const fetchRoomTypes = useCallback(async () => {
    if (!hotelId) return;

    setPageLoading(true);
    try {
      const res = await axiosInstance.get(`/RoomTypes/GetRoomTypes/${hotelId}`);
      setRoomTypes(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      notification.error({
        title: "Error",
        description: "Failed to fetch room types.",
        placement: "topRight",
      });
      setRoomTypes([]);
    } finally {
      setPageLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    if (hotelId) fetchRoomTypes();
  }, [hotelId, fetchRoomTypes]);

  const openAdd = useCallback(() => {
    setCurrentRoomType(null);
    setTypeName("");
    setDescription("");
    setEditModalOpen(true);
  }, []);

  const openEdit = useCallback((record) => {
    setCurrentRoomType(record);
    setTypeName(record?.typeName ?? "");
    setDescription(record?.description ?? "");
    setEditModalOpen(true);
  }, []);

  const closeEdit = useCallback(() => {
    setEditModalOpen(false);
    setCurrentRoomType(null);
    setTypeName("");
    setDescription("");
  }, []);

  const handleDelete = useCallback(
    (roomTypeId) => {
      confirm({
        title: "Delete Room Type",
        content: "Are you sure you want to delete this room type?",
        okText: "Delete",
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await axiosInstance.delete(`/RoomTypes/DeleteRoomType/${roomTypeId}`);
            notification.success({
              title: "Success",
              description: "Room type deleted successfully.",
              placement: "topRight",
            });
            fetchRoomTypes();
          } catch {
            notification.error({
              title: "Error",
              description: "Failed to delete room type.",
              placement: "topRight",
            });
          }
        },
      });
    },
    [fetchRoomTypes]
  );

  const handleBatchDelete = useCallback(() => {
    confirm({
      title: `Delete ${selectedRowKeys.length} room type(s)?`,
      content: "Are you sure you want to delete these room types?",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await Promise.all(
            selectedRowKeys.map((id) =>
              axiosInstance.delete(`/RoomTypes/DeleteRoomType/${id}`)
            )
          );
          notification.success({
            title: "Success",
            description: `${selectedRowKeys.length} room type(s) deleted successfully.`,
            placement: "topRight",
          });
          setSelectedRowKeys([]);
          fetchRoomTypes();
        } catch {
          notification.error({
            title: "Error",
            description: "Failed to delete room types.",
            placement: "topRight",
          });
        }
      },
    });
  }, [selectedRowKeys, fetchRoomTypes]);

  const handleSave = useCallback(async () => {
    if (!hotelId) return;

    const name = String(typeName || "").trim();
    if (!name) {
      notification.warning({
        title: "Validation",
        description: "Room type name is required.",
        placement: "topRight",
      });
      return;
    }

    setSaving(true);
    try {
      if (currentRoomType?.roomTypeId) {
        await axiosInstance.put(
          `/RoomTypes/UpdateRoomType/${currentRoomType.roomTypeId}`,
          {
            hotelId,
            typeName: name,
            description: String(description || "").trim(),
          }
        );
        notification.success({
          title: "Success",
          description: "Room type updated successfully.",
          placement: "topRight",
        });
      } else {
        await axiosInstance.post(`/RoomTypes/AddRoomType`, {
          hotelId,
          typeName: name,
          description: String(description || "").trim(),
        });
        notification.success({
          title: "Success",
          description: "Room type added successfully.",
          placement: "topRight",
        });
      }

      closeEdit();
      fetchRoomTypes();
    } catch (error) {
      notification.error({
        title: "Error",
        description: error?.response?.data?.message || "Failed to save room type.",
        placement: "topRight",
      });
    } finally {
      setSaving(false);
    }
  }, [hotelId, typeName, description, currentRoomType, closeEdit, fetchRoomTypes]);

  const filteredRoomTypes = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return roomTypes;

    return roomTypes.filter((rt) => {
      const n = String(rt?.typeName ?? "").toLowerCase();
      const d = String(rt?.description ?? "").toLowerCase();
      return n.includes(q) || d.includes(q);
    });
  }, [roomTypes, searchText]);

  const hasSelected = selectedRowKeys.length > 0;

  const columns = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "typeName",
        key: "typeName",
        width: 220,
        ellipsis: true,
        render: (v, r) => (
          <Space size={6}>
            <Tag color="blue">#{r?.roomTypeId}</Tag>
            <span title={v}>{v || "N/A"}</span>
          </Space>
        ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        ellipsis: true,
        render: (v) => <span title={v}>{v || "—"}</span>,
      },
      {
        title: "",
        key: "action",
        fixed: "right",
        width: 140,
        render: (_, record) => (
          <Space size={6} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
              />
            </Tooltip>

            <Popconfirm
              title="Delete this room type?"
              description="This action cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record?.roomTypeId)}
            >
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [openEdit, handleDelete]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
    }),
    [selectedRowKeys]
  );

  return (
    <div className="room-types-page" style={{ padding: 16 }}>
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 12 }} />

      {/* Header Card (same structure as RoomListPage) */}
      <Card className="rt-header-card" variant="outlined">
        <div className="rt-header-row">
          <div className="rt-header-title">
            <Space size={8} align="center">
              <Title level={5} style={{ margin: 0 }}>
                Room Types
              </Title>
              <Badge count={roomTypes.length} showZero />
              <Tooltip title="Guide">
                <Button
                  size="small"
                  icon={<InfoCircleOutlined />}
                  onClick={() => setShowGuide(true)}
                />
              </Tooltip>
            </Space>

            <Text type="secondary">
              Define categories (Standard, Deluxe, Suite) before pricing and rooms.
            </Text>

            <div className="rt-filters">
              <Input
                placeholder="Search room types…"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 260 }}
              />
            </div>
          </div>

          <div className="rt-actions">
            {hasSelected && (
              <Button
                size="small"
                danger
                onClick={handleBatchDelete}
                icon={<DeleteOutlined />}
              >
                Delete Selected ({selectedRowKeys.length})
              </Button>
            )}

            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAdd}
            >
              Add Room Type
            </Button>

            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchRoomTypes}
              loading={pageLoading}
            >
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      <div style={{ height: 12 }} />

      {/* Table Card (same as RoomListPage) */}
      <Card variant="outlined" styles={{ body: { padding: 12 } }}>
        <Table
          className="rt-slim-table"
          size="small"
          rowKey="roomTypeId"
          rowSelection={{ type: "checkbox", ...rowSelection }}
          columns={columns}
          dataSource={filteredRoomTypes}
          loading={pageLoading}
          tableLayout="fixed"
          scroll={{ x: 900 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total) =>
              `Total ${total} room type${total === 1 ? "" : "s"}`,
          }}
          onRow={(record) => ({
            onClick: () => openEdit(record),
          })}
          locale={{
            emptyText: (
              <Space orientation="vertical">
                <Text>No room types found</Text>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                  Add Room Type
                </Button>
              </Space>
            ),
          }}
        />
      </Card>

      {/* Guide Modal */}
      <Modal
        title="Room Types Guide"
        open={showGuide}
        onCancel={() => setShowGuide(false)}
        destroyOnHidden
        footer={[
          <Button key="close" size="small" onClick={() => setShowGuide(false)}>
            Close
          </Button>,
        ]}
      >
        <p>This module allows you to manage room types in your hotel.</p>
        <h4>Key Features:</h4>
        <ul>
          <li>Define categories of rooms (e.g., Standard, Deluxe, Suite)</li>
          <li>Set descriptions for each room type</li>
          <li>Create room types before setting prices and adding rooms</li>
        </ul>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        title={currentRoomType ? "Edit Room Type" : "Add Room Type"}
        open={editModalOpen}
        onCancel={closeEdit}
        destroyOnHidden
        confirmLoading={saving}
        okText={currentRoomType ? "Update" : "Save"}
        onOk={handleSave}
      >
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <Text strong>Room Type Name</Text>
            <Input
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              placeholder="e.g., Standard, Deluxe, Suite"
              style={{ marginTop: 6 }}
            />
          </div>

          <div>
            <Text strong>Description</Text>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              rows={4}
              style={{ marginTop: 6 }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RoomTypesPage;
